import { css } from '@emotion/react';
import {
  Button,
  color,
  Modal,
  RadioButton,
  spacing,
  Typography
} from '@uniquegood/realworld-studio-design';
import { useCallback, useState } from 'react';

import { useParamsWithMatchPath, useRequest } from '@/hooks';
import { useCopyToClipboard } from '@/components/ActionHandler/hooks';
import { onMobile } from '@/styles/responsive';
import { toast } from '@/utils';

const PLAY_LINK_BASE = 'https://web.realworld.to/projects';

const ANONYMOUS_QUERY = '?useAnonymous=true';

type PlayLinkType = 'anonymous' | 'normal';

const RADIO_NAME = 'copy-play-link-type';

function buildPlayUrl(projectId: string, type: PlayLinkType) {
  const baseUrl = `${PLAY_LINK_BASE}/${encodeURIComponent(projectId)}/play`;
  return type === 'anonymous' ? `${baseUrl}${ANONYMOUS_QUERY}` : baseUrl;
}

export default function CopyPlayLinkButton() {
  const { appId, projectId } = useParamsWithMatchPath();

  const { data: releaseStatusData } = useRequest<CommonResponseModel<ReleaseStatus>>(
    appId && projectId ? `/apps/${appId}/projects/${projectId}/releaseStatus` : null
  );

  const [, copy] = useCopyToClipboard();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<PlayLinkType>('anonymous');

  const isReleased = releaseStatusData?.data.status === 'Released';

  const openModal = useCallback(() => {
    setSelectedType('anonymous');
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleChangeType = useCallback((value: PlayLinkType) => {
    setSelectedType(value);
  }, []);

  const handleCopy = useCallback(async () => {
    if (!projectId) return;
    const url = buildPlayUrl(projectId, selectedType);
    const ok = await copy(url);
    if (ok) {
      toast({
        message:
          selectedType === 'anonymous'
            ? '익명 플레이 링크를 복사했어요'
            : '플레이 링크를 복사했어요'
      });
      closeModal();
    } else {
      toast({ message: '복사에 실패했어요. 다시 시도해 주세요.', type: 'error' });
    }
  }, [closeModal, copy, projectId, selectedType]);

  if (!isReleased || !projectId) {
    return null;
  }

  return (
    <>
      <Button type="plain" icon="link_solid" cssStyle={CopyPlayLinkButtonStyle} onClick={openModal}>
        <span css={LabelStyle}>플레이 링크 복사</span>
      </Button>
      <Modal
        open={isModalOpen}
        onClose={closeModal}
        title="플레이 링크 복사"
        hasCloseIcon
        canBackdropClose
        primaryAction={{ content: '복사하기', onAction: handleCopy }}
        secondaryAction={{ content: '취소', onAction: closeModal }}
      >
        <div css={OptionListStyle}>
          <div css={[OptionStyle, selectedType === 'anonymous' && OptionSelectedStyle]}>
            <RadioButton
              name={RADIO_NAME}
              value="anonymous"
              checked={selectedType === 'anonymous'}
              onChange={handleChangeType}
            >
              <div css={OptionTextStyle}>
                <Typography as="span" type="body" cssStyle={OptionTitleStyle}>
                  익명 플레이
                </Typography>
                <Typography as="span" type="caption" textColor="subdued" cssStyle={OptionTitleStyle}>
                  로그인 없이 누구나 플레이
                </Typography>
              </div>
            </RadioButton>
          </div>
          <div css={[OptionStyle, selectedType === 'normal' && OptionSelectedStyle]}>
            <RadioButton
              name={RADIO_NAME}
              value="normal"
              checked={selectedType === 'normal'}
              onChange={handleChangeType}
            >
              <div css={OptionTextStyle}>
                <Typography as="span" type="body" cssStyle={OptionTitleStyle}>
                  일반 플레이
                </Typography>
                <Typography as="span" type="caption" textColor="subdued" cssStyle={OptionTitleStyle}>
                  로그인 필요
                </Typography>
              </div>
            </RadioButton>
          </div>
        </div>
      </Modal>
    </>
  );
}

const CopyPlayLinkButtonStyle = css`
  height: 36px;
  padding: 8px 12px;
`;

const LabelStyle = css`
  ${onMobile} {
    display: none;
  }
`;

const OptionListStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.small};
`;

const OptionStyle = css`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.common.small};
  padding: ${spacing.common.medium};
  border: 1px solid ${color.border_default_subdued};
  border-radius: ${spacing.borderRadius.medium};
  cursor: pointer;
`;

const OptionSelectedStyle = css`
  border-color: ${color.interactive_primary_default};
  background-color: ${color.surface_blue_default};
`;

const OptionTextStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OptionTitleStyle = css`
  margin: 0;
`;
